/**
 * BusinessCollectionPage — /collection/:id
 *
 * A public, shareable page for one curated collection: banner, oversized title
 * overlapping it, sticky filter chips, then a grid of business cards.
 *
 * Public on purpose — a scanned QR has to open for people who aren't logged in.
 *
 * The page is its own scroller (banner, title, chips and grid scroll together);
 * only the 92px top bar sits above it, fading its plate + compact title in as
 * the big <h1> scrolls away.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Pencil, Share2, Trash2 } from "lucide-react";
import { AppDispatch, RootState } from "../../../dataManagement/uiDb/UiDb";
import { fetchCollectionById, deleteCollection } from "../../../services/thunks/BusinessThunk";
import {
  setCollectionMeta,
  clearCollection,
  selectCollectionBusinesses,
  selectCollectionMeta,
  selectCollectionStatus,
  selectCollectionTypeFilters,
  selectDeleteCollectionStatus,
} from "../../../dataManagement/uiDb/BusinessCollectionDb";
import { BusinessProfile } from "../../../interfaces/BusinessObject";
import { BusinessCollection } from "../../../interfaces/BusinessCollectionObject";
import { buildBusinessPath } from "../../../services/helpers/BusinessSlugHelper";
import { splitTitle, formatUpdated } from "../../../services/helpers/CollectionDisplayHelper";
import ShareProfileModal from "../../shared/ShareProfileModal/ShareProfileModal";
import WhatsAppIcon from "../../shared/icons/WhatsAppIcon";
import CollectionBusinessCard from "./childComponents/CollectionBusinessCard";
import genericUtility from "../../../services/utility/GenericUtility";
import { useUserPhoneById } from "../../../hooks/useUserPhoneById";
import { useNotification } from "../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../constants/Enums";
import ConfirmModal from "../../shared/ConfirmModal/ConfirmModal";
import CreateCollectionModal from "../../features/Admin/BusinessCollections/CreateCollectionModal";
import "./styles/BusinessCollectionPage.css";

interface BusinessCollectionPageProps {
  /** Grid column count — 2 on mobile by design. */
  columns?: number;
}

/** Title fallback while the collection's own metadata is unavailable. */
const FALLBACK_TITLE = "Curated Collection";

/** Who to contact to get listed — surfaced in the footer as a WhatsApp link. */
const CONTACT_PHONE_DISPLAY = "+91 97049 87649";
const CONTACT_PHONE_DIGITS = "9704987649";

const BusinessCollectionPage: React.FC<BusinessCollectionPageProps> = ({
  columns = 2,
}) => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch: AppDispatch = useDispatch();
  const { triggerNotification } = useNotification();

  const businesses = useSelector(selectCollectionBusinesses);
  const { isLoading, hasLoaded, error, notFound } = useSelector(
    selectCollectionStatus
  );
  const typeFilters = useSelector(selectCollectionTypeFilters);
  const collection = useSelector((state: RootState) =>
    selectCollectionMeta(state, id)
  );
  const { isDeleting } = useSelector((state: RootState) =>
    selectDeleteCollectionStatus(state, id)
  );

  // Chip selection is local — it filters the loaded grid, never refetches.
  const [activeType, setActiveType] = useState("");
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [barProgress, setBarProgress] = useState(0);
  const [chipsStuck, setChipsStuck] = useState(false);

  // Edit/delete are creator-only — no separate isLead check here, matching
  // CreateBusinessModal's "effective owner" affordance on the business page.
  const currentUserId = genericUtility.getCurrentUserId();
  const isOwner = !!collection && collection.creatorId === currentUserId;
  const creatorPhone = useUserPhoneById(collection?.creatorId);

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteCollection(id)).unwrap();
      triggerNotification("Collection deleted.", NotificationType.SUCCESS);
      setIsDeleteConfirmOpen(false);
      navigate(backTo ?? "/my-profile?tab=admin&section=myCollections");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't delete this collection.";
      triggerNotification(msg, NotificationType.ERROR);
      setIsDeleteConfirmOpen(false);
    }
  };

  const scrollerRef = useRef<HTMLDivElement>(null);
  const chipsRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);
  const chipsStuckRef = useRef(false);

  // Seed metadata from the search result the user tapped, when there is one.
  const routerState = location.state as
    | { collection?: BusinessCollection; backTo?: string }
    | null;
  const passedCollection = routerState?.collection;
  // Where the back button should return to. Plain browser history (navigate(-1))
  // loses "Admin > My Collections" because Profile.tsx doesn't sync its tab
  // state to the URL — callers that need a precise return path (e.g.
  // BusinessCollectionsProgram) pass one explicitly via router state.
  const backTo = routerState?.backTo;

  // window.history.length <= 1 means the page was opened directly (QR scan,
  // shared link, typed URL) with nothing for navigate(-1) to go back to —
  // same convention used by ProductDetails/CompactHeader/CartPage's back
  // buttons elsewhere in the app.
  const handleBack = () => {
    if (backTo) {
      navigate(backTo);
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/home");
    }
  };

  useEffect(() => {
    if (passedCollection) dispatch(setCollectionMeta(passedCollection));
  }, [dispatch, passedCollection]);

  // One call returns the collection's metadata and its members; the grid needs
  // the members' details, so they're requested inline.
  const load = useCallback(() => {
    if (id) {
      dispatch(
        fetchCollectionById({ collectionId: id, includeBusinessDetails: true })
      );
    }
  }, [dispatch, id]);

  useEffect(() => {
    load();
  }, [load]);

  // Drop the previous collection on the way out, so navigating collection →
  // collection can't flash the old grid (or a stale not-found) before the next
  // fetch resolves.
  useEffect(() => () => { dispatch(clearCollection()); }, [dispatch]);

  // Reset the chip if a reload drops the type it was pinned to.
  useEffect(() => {
    if (activeType && !typeFilters.some((f) => f.id === activeType)) {
      setActiveType("");
    }
  }, [typeFilters, activeType]);

  // Bar fade: 0 → 1 across scrollTop 150 → 240. Only re-renders on a
  // meaningful change.
  const handleScroll = useCallback(() => {
    const top = scrollerRef.current?.scrollTop ?? 0;
    const t = Math.max(0, Math.min(1, (top - 150) / 90));
    if (Math.abs(t - progressRef.current) > 0.01 || t === 0 || t === 1) {
      progressRef.current = t;
      setBarProgress(t);
    }

    // The chips stick later than the bar fades (the title block is tall), so
    // their plate is driven by where they actually are, not by `t`.
    const stuck = (chipsRef.current?.getBoundingClientRect().top ?? Infinity) <= 93;
    if (stuck !== chipsStuckRef.current) {
      chipsStuckRef.current = stuck;
      setChipsStuck(stuck);
    }
  }, []);

  const visibleBusinesses = useMemo(
    () =>
      activeType
        ? businesses.filter((b) => b.listingType === activeType)
        : businesses,
    [businesses, activeType]
  );

  const openBusiness = useCallback(
    (business: BusinessProfile) =>
      navigate(buildBusinessPath(business.businessId, business.name)),
    [navigate]
  );

  const title = collection?.name || FALLBACK_TITLE;
  const { head: titleHead, tail: titleTail, accentTail } = splitTitle(title);
  const hasBanner = Boolean(collection?.bannerUrl);
  // Real per-collection contact: the creator's own number, resolved via
  // useUserPhoneById. Falls back to the generic team number until that
  // resolves (or for collections whose creator lookup fails) — display-only,
  // there's no field in the collection payload to persist an override.
  const contactPhoneDisplay = creatorPhone ?? CONTACT_PHONE_DISPLAY;
  const contactPhoneDigits = (creatorPhone ?? CONTACT_PHONE_DIGITS).replace(/\D/g, "");
  const contactUrl = `https://wa.me/+91${contactPhoneDigits}?text=${encodeURIComponent(
    `Hi! I'd like to create my own NineFinds catalogue and be added to the "${title}" collection.`
  )}`;
  const count = hasLoaded ? businesses.length : collection?.businessIds.length ?? 0;
  const updated = formatUpdated(collection?.updatedAt);
  const shareUrl = `${window.location.origin}/collection/${id}`;
  const gridStyle = {
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
  };

  return (
    <div className="coll-screen">
      {/* ── Top bar (above the scroller) ─────────────────────────────────── */}
      <div className="coll-bar">
        <div className="coll-bar-plate" style={{ opacity: barProgress }} />

        <div className="coll-bar-row">
          <button
            type="button"
            className="coll-bar-btn"
            onClick={handleBack}
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>

          <span
            className="coll-bar-title"
            style={{
              opacity: barProgress,
              transform: `translateY(${(1 - barProgress) * 6}px)`,
            }}
            aria-hidden={barProgress < 0.5}
          >
            {title}
          </span>

          <div className="coll-bar-actions">
            {isOwner && (
              <>
                <button
                  type="button"
                  className="coll-bar-btn"
                  onClick={() => setIsEditOpen(true)}
                  aria-label="Edit this collection"
                >
                  <Pencil size={17} />
                </button>
                <button
                  type="button"
                  className="coll-bar-btn"
                  onClick={() => setIsDeleteConfirmOpen(true)}
                  aria-label="Delete this collection"
                >
                  <Trash2 size={17} />
                </button>
              </>
            )}
            <button
              type="button"
              className="coll-bar-btn"
              onClick={() => setIsShareOpen(true)}
              aria-label="Share this collection"
            >
              <Share2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Scroller ─────────────────────────────────────────────────────── */}
      <div className="coll-scroll" ref={scrollerRef} onScroll={handleScroll}>
        {/* Without an image the banner shrinks — a 296px pastel void reads as
            a loading failure rather than as design. */}
        <div className={`coll-banner${hasBanner ? "" : " coll-banner--empty"}`}>
          {collection?.bannerUrl && (
            <img src={collection.bannerUrl} alt="" loading="eager" />
          )}
          <div className="coll-banner-scrim" />
        </div>

        <div className="coll-title-block">
          <div className="coll-kicker">
            <span className="coll-kicker-bar" />
            Curated collection
          </div>

          <h1 className="coll-h1">
            {titleHead && (
              <>
                {titleHead}
                <br />
              </>
            )}
            <span className={accentTail ? "coll-h1-accent" : undefined}>
              {titleTail}
            </span>
          </h1>

          <div className="coll-meta">
            <span className="coll-meta-strong">
              {count} {count === 1 ? "business" : "businesses"}
            </span>
            {collection?.creatorName && (
              <>
                <span className="coll-meta-sep">/</span>
                <span>Curated by {collection.creatorName}</span>
              </>
            )}
            {updated && (
              <>
                <span className="coll-meta-sep">/</span>
                <span>Updated {updated}</span>
              </>
            )}
          </div>
        </div>

        {/* Chips only once there's something to filter. */}
        {hasLoaded && businesses.length > 0 && (
          <div
            className={`coll-chips${chipsStuck ? " coll-chips--stuck" : ""}`}
            ref={chipsRef}
          >
            <div className="coll-chips-row">
              {typeFilters.map((filter) => (
                <button
                  key={filter.id || "all"}
                  type="button"
                  className={`coll-chip${
                    activeType === filter.id ? " coll-chip--active" : ""
                  }`}
                  onClick={() => setActiveType(filter.id)}
                  aria-pressed={activeType === filter.id}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="coll-content">
          {isLoading && (
            <div className="coll-grid" style={gridStyle} aria-hidden="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="coll-skeleton" key={i}>
                  <div className="coll-skeleton-logo" />
                  <div className="coll-skeleton-line" />
                  <div className="coll-skeleton-line coll-skeleton-line--short" />
                </div>
              ))}
            </div>
          )}

          {/* A wrong id can't be retried away. */}
          {!isLoading && notFound && (
            <div className="coll-state" role="alert">
              <p className="coll-state-text">
                This collection may have been removed, or the link is incorrect.
              </p>
              <button
                type="button"
                className="coll-state-btn"
                onClick={() => navigate("/search")}
              >
                Search collections
              </button>
            </div>
          )}

          {!isLoading && error && !notFound && (
            <div className="coll-state" role="alert">
              <p className="coll-state-text">{error}</p>
              <button type="button" className="coll-state-btn" onClick={load}>
                Try again
              </button>
            </div>
          )}

          {!isLoading && !error && businesses.length === 0 && (
            <div className="coll-state">
              <p className="coll-state-text">
                No businesses in this collection yet.
              </p>
              <button
                type="button"
                className="coll-state-btn"
                onClick={() => navigate("/search")}
              >
                Explore businesses
              </button>
            </div>
          )}

          {!isLoading && !error && visibleBusinesses.length > 0 && (
            <div className="coll-grid" style={gridStyle}>
              {visibleBusinesses.map((business) => (
                <CollectionBusinessCard
                  key={business.businessId}
                  business={business}
                  onOpen={openBusiness}
                />
              ))}
            </div>
          )}

          {/* Chip filtered everything out — the collection itself isn't empty. */}
          {!isLoading &&
            !error &&
            businesses.length > 0 &&
            visibleBusinesses.length === 0 && (
              <div className="coll-state">
                <p className="coll-state-text">Nothing here under that filter.</p>
                <button
                  type="button"
                  className="coll-state-btn"
                  onClick={() => setActiveType("")}
                >
                  Show all
                </button>
              </div>
            )}
        </div>

        <div className="coll-footer">
          <span className="coll-footer-rule" />
          <p className="coll-footer-text">
            Contact{" "}
            <a
              className="coll-footer-wa"
              href={contactUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <WhatsAppIcon size={13} />
              {contactPhoneDisplay}
            </a>{" "}
            to create your own catalogue and add it to this collection.
          </p>
        </div>
      </div>

      <ShareProfileModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        profileUrl={shareUrl}
        title={title}
        subtitle={`Check out ${title} on NineFinds`}
        shareMode="qr"
      />

      {isOwner && collection && (
        <CreateCollectionModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          editCollection={{ collection, businesses }}
          // updateCollection's response only carries businessIds, not resolved
          // BusinessProfile records — refetch so the grid picks up any newly
          // added/removed businesses without requiring a manual page refresh.
          onSaved={load}
        />
      )}

      <ConfirmModal
        isOpen={isDeleteConfirmOpen}
        tone="danger"
        title="Delete this collection?"
        message={
          <>
            This will permanently remove <strong>{title}</strong>. This cannot
            be undone.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteConfirmOpen(false)}
      />
    </div>
  );
};

export default BusinessCollectionPage;
